import type { GeoEditOperation, GeoEditOptions, EditGeodataRequest } from '@visualizer/types';
import { callFileManager } from '@api/fileManagerApi';

export interface GeoEditAdapter {
    apply(op: GeoEditOperation): void;
    revert(op: GeoEditOperation): void;
}

/**
 * Originator / caretaker of geo edit commands.
 * UI calls applyOperation(), undo(), redo().
 * When saving, it emits a File Manager request.
 */
export class GeoEditSession {
    private operations: GeoEditOperation[] = [];
    private undone: GeoEditOperation[] = [];

    constructor(private readonly adapter: GeoEditAdapter) {}

    getOperations(): readonly GeoEditOperation[] {
        return this.operations;
    }

    /**
     * Apply a new command and record it in history.
     * Clears the redo stack.
     */
    applyOperation(op: GeoEditOperation) {
        this.adapter.apply(op);
        this.operations.push(op);
        this.undone.length = 0;
    }

    undo() {
        const op = this.operations.pop();
        if (!op) return;
        this.adapter.revert(op);
        this.undone.push(op);
    }

    redo() {
        const op = this.undone.pop();
        if (!op) return;
        this.adapter.apply(op);
        this.operations.push(op);
    }

    clear() {
        this.operations = [];
        this.undone = [];
    }

    toFileManagerRequest(newGeodataId?: string, newGeodataName?: string): EditGeodataRequest {
        const options: GeoEditOptions = {
            operations: this.operations,
            ...(newGeodataId ? { newGeodataId } : {}),
            ...(newGeodataName ? { newGeodataName } : {}),
        };

        return {
            dataCategory: 'GEOMETRY',
            instruction: 'EDIT_GEODATA',
            options,
        };
    }

    async saveToServer(newGeodataId?: string, newGeodataName?: string) {
        const request = this.toFileManagerRequest(newGeodataId, newGeodataName);
        return await callFileManager(request);
    }
}
